import React, { ChangeEvent, useCallback, useContext, useMemo, useState } from 'react';
import { useDebounce } from 'use-debounce';
import { AddButton, ReactTableContext, ReactTableContextProvider } from '@td-client-frontend/common';
import { WithInfinityQuery } from '../../@types/common';
import { ManageRoleType, RoleToSend } from '../../@types/Roles';
import { Button } from '../../components/Button';
import Modal from '../../components/Modal/NewModal';
import WithPermission from '../../components/WithPermission';
import { useAuth } from '../../context/AuthContext';
import useRoles from '../../hooks/useRoles';
import ManageRole from '../ManageRole';
import ManageRolesTable from './ManageRolesTable';
import { SearchTextInput } from '../../components/SearchTextInput';
import { Permissions } from '../../@types/Permissions';

function ManageRoles() {
  const { checkPerm } = useAuth();
  const {
    state: { sortBy }
  } = useContext(ReactTableContext);
  const [search, setSearch] = useState<string>('');
  const [debouncedSearch] = useDebounce<string>(search, 2000);
  const [selectedRole, setSelectedRole] = useState<string | null>(null);
  const [showNewRoleModal, setShowNewRoleModal] = useState<boolean>(false);
  const [showToggleStatusModal, setShowToggleStatusModal] = useState<boolean>(false);
  const [checkedRoles, setCheckedRoles] = useState<string[]>([]);
  const {
    data: clientRepsRole,
    deactivateRoles,
    isFetching
  } = useRoles({
    sorting: sortBy[0]?.desc ? 'DESC' : 'ASC',
    name: debouncedSearch
  });

  const rolesData: ManageRoleType[] = useMemo(() => {
    return (
      clientRepsRole?.pages.reduce((acc: ManageRoleType[], page: WithInfinityQuery<ManageRoleType>) => {
        const nodes: ManageRoleType[] = page?.edges?.map(({ node }) => node) || [];
        return [...acc, ...nodes];
      }, []) || []
    );
  }, [clientRepsRole]);

  const onMainCheck = useCallback(() => {
    const isAllRolesSelected: boolean = !!(rolesData.length && rolesData.length === checkedRoles.length);
    return !isAllRolesSelected ? setCheckedRoles(rolesData.map((role) => role.id)) : setCheckedRoles([]);
  }, [checkedRoles.length, rolesData]);

  const onRoleSelect = (checked: boolean, id: string) => {
    return !checked
      ? setCheckedRoles((prev) => [...prev, id])
      : setCheckedRoles((prev) => prev.filter((roleId) => roleId !== id));
  };
  const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  };

  const onRoleManage = (id: string) => {
    setShowNewRoleModal(true);
    setSelectedRole(id);
  };

  const onAdd = () => {
    setSelectedRole(null);
    setShowNewRoleModal(true);
  };

  const toggleStatus = () => {
    setShowToggleStatusModal(true);
  };

  const isNewRoleFlow = selectedRole === null;
  const headerLabel = isNewRoleFlow ? 'Add Role' : checkPerm(Permissions.RoleWrite) ? 'Edit Role' : 'Role Details';
  const toggleNewRoleModal = () => setShowNewRoleModal(!showNewRoleModal);
  const toggleRoleStatusModal = () => setShowToggleStatusModal(!showToggleStatusModal);

  const handleToggleStatusClick = async () => {
    const rolesToMutate = rolesData.reduce((acc: RoleToSend[], { id, roleNm, userCount, grants, active, ...rest }) => {
      if (checkedRoles.includes(id)) acc.push({ id, name: roleNm, active: !active, ...rest });
      return acc;
    }, []);
    const result = await deactivateRoles(rolesToMutate);
    if (result?.status === 200) {
      setCheckedRoles([]);
      toggleRoleStatusModal();
    }
  };

  return (
    <div className="flex flex-col items-center justify-between w-full px-6 py-4 pt-10 mx-auto bg-white sm:items-baseline">
      <Modal
        isOpen={showToggleStatusModal}
        onClose={toggleRoleStatusModal}
        contentLabel="Toggle Roles Status Modal"
        title="Toggle Roles Status Modal"
        header="Toggle Roles Status"
        size="prompt"
        actions={[
          { title: 'No', kind: 'secondary' },
          {
            title: 'Yes',
            kind: 'primary',
            onClick: handleToggleStatusClick
          }
        ]}
      >
        <section className="pb-6 pl-6 font-medium">
          <p>Are you sure you wish to toggle {checkedRoles.length} role(s) status?</p>
        </section>
      </Modal>

      <Modal
        isOpen={showNewRoleModal}
        onClose={toggleNewRoleModal}
        contentLabel={`${headerLabel} Modal`}
        title={`${headerLabel} Modal`}
        header={headerLabel}
        size="large"
      >
        <ManageRole id={selectedRole} onRequestClose={toggleNewRoleModal} />
      </Modal>

      <div className="flex flex-col w-full flex-1 overflow-hidden">
        <div className="flex items-center justify-between w-full px-6 my-8">
          <div className="font-effra">
            <h1 className="text-4xl">Manage roles</h1>
          </div>
          <WithPermission requiredPerms={['RoleCreate']}>
            <AddButton onClick={onAdd} label="Add role" data-testid="manageroles-addrole" />
          </WithPermission>
        </div>
        <div className="w-1/4 px-1">
          <SearchTextInput
            value={search}
            onChange={handleSearchChange}
            onClear={() => setSearch('')}
            placeholder="Search..."
          />
        </div>
        <ManageRolesTable
          rolesData={rolesData}
          onRoleManage={onRoleManage}
          isFetching={isFetching}
          checkedRoles={checkedRoles}
          onMainCheck={onMainCheck}
          onRoleSelect={onRoleSelect}
        />
        <div className="flex flex-col pb-16 sm:flex-row sm:flex-wrap justify-end bottom-0 right-0 px-6 py-5 w-full bg-white z-50 transition-all duration-150">
          <WithPermission requiredPerms={['RoleWrite']}>
            <Button name="toggle-btn" title="Toggle Status" onClick={toggleStatus} disabled={!checkedRoles.length} />
          </WithPermission>
        </div>
      </div>
    </div>
  );
}

const ManageRolesWithTableProvider = (props: any) => (
  <ReactTableContextProvider>
    <ManageRoles {...props} />
  </ReactTableContextProvider>
);

export default ManageRolesWithTableProvider;
